import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { AsyncPipe, CommonModule } from "@angular/common";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { FavoritesComponent } from "./favorites/favorites.component";
import { FavoritesService } from "../../../../../shared/src/lib/services/favorites.service";
import { combineLatest, Observable, Subject } from "rxjs";
import { NavService } from "../../../../../shared/src/lib/services/nav.service";
import { MarketService } from "../../../../../shared/src/lib/services/market.service";
import { TranslateModule } from "@ngx-translate/core";
import { LoaderService } from "../../../../../shared/src/lib/services/loader.service";
import { map, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-sports-bar",
  templateUrl: "./sports-bar.component.html",
  styleUrl: "./sports-bar.component.scss",
  imports: [
    CommonModule,
    MatExpansionModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    FavoritesComponent,
    TranslateModule,
    AsyncPipe,
  ],
})
export class SportsBarComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  favoritesService = inject(FavoritesService);
  marketService = inject(MarketService);
  navService = inject(NavService);
  loader = inject(LoaderService);
  hasFavorites = false;
  $loading!: Observable<boolean>;
  showSearch = false;
  isSortedAZ = false;
  localSportsPanelOpen = true;
  localFavoritesPanelOpen = false;

  private readonly sportIdNames: Record<string, string> = {
    "1": "Soccer", "2": "Tennis", "4": "Cricket",
    "7": "Horse Racing", "7552": "Basketball", "4339": "Greyhounds",
  };

  private readonly sportRoutes: Record<string, string> = {
    "1": "soccer", "2": "tennis", "4": "cricket",
    "7": "horse", "7552": "basketball", "4339": "greyhound",
  };

  topLeagues$ = combineLatest([
    this.marketService.promoted$,
    this.marketService.sports$,
  ]).pipe(
    map(([data, sports]) => {
      const seen = new Set<string>();
      const leagues = ((data?.promotedLeagues || []) as any[]).filter((l: any) => {
        if (!(l.markets || []).length) return false;
        const key = String(l.leagueId);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (!leagues.length) return [];

      const dynamicSportMap = new Map(sports.map((s: any) => [String(s.id), s.name]));
      const groups = new Map<string, { sport: string; sportRoute: string; leagues: any[] }>();

      leagues.forEach((league: any) => {
        const sportId = String(league.sportId);
        const sportName =
          league.sport ||
          this.sportIdNames[sportId] ||
          dynamicSportMap.get(sportId) ||
          league.leagueName?.split(" / ")?.[0] ||
          "Other";
        const sportRoute = this.sportRoutes[sportId] || sportName.toLowerCase().replace(/\s+/g, "-");
        const groupKey = (sportId !== "null" && sportId !== "undefined") ? sportId : sportName;

        if (!groups.has(groupKey)) groups.set(groupKey, { sport: sportName, sportRoute, leagues: [] });
        groups.get(groupKey)!.leagues.push(league);
      });

      return Array.from(groups.values());
    }),
  );

  ngOnInit() {
    this.$loading = combineLatest([
      this.loader.isLoading("/v1/api/navigation_list"),
      this.loader.isLoading("/navigation/event"),
    ]).pipe(map(([a, b]) => a || b));

    this.favoritesService.getFavoriteItems().pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.hasFavorites = items.length > 0;
    });

    this.navService.openSportsPanel.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.localSportsPanelOpen = open;
    });

    this.navService.openFavoritesPanel.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.localFavoritesPanelOpen = open;
    });
  }

  handleClick() {
    if (window.innerWidth < 1280) this.navService.openSportsBar.next(false);
  }

  stopPropagation(e: Event) { e.stopPropagation(); }

  handleSportsPanel(state: string) {
    this.localSportsPanelOpen = state === "opened";
    Promise.resolve().then(() => {
      if (state === "opened") {
        this.navService.setOpenSportsPanel(true);
        this.navService.setOpenFavoritesPanel(false);
      } else {
        this.navService.setOpenSportsPanel(false);
      }
    });
  }

  handleFavoritesPanel(state: string) {
    this.localFavoritesPanelOpen = state === "opened";
    Promise.resolve().then(() => {
      if (state === "opened") {
        this.navService.setOpenFavoritesPanel(true);
        this.navService.setOpenSportsPanel(false);
      } else {
        this.navService.setOpenFavoritesPanel(false);
        this.navService.setOpenSportsPanel(true);
      }
    });
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
    if (this.showSearch) {
      this.isSortedAZ = false;
      this.navService.sportsSortAZ.next(false);
    } else {
      this.navService.sportsSearchQuery.next("");
    }
  }

  updateSearch(query: string) {
    this.navService.sportsSearchQuery.next(query);
  }

  toggleSort() {
    this.isSortedAZ = !this.isSortedAZ;
    if (this.isSortedAZ) {
      this.showSearch = false;
      this.navService.sportsSearchQuery.next("");
    }
    this.navService.sportsSortAZ.next(this.isSortedAZ);
  }

  resetFilters() {
    this.showSearch = false;
    this.isSortedAZ = false;
    this.navService.sportsSearchQuery.next("");
    this.navService.sportsSortAZ.next(false);
  }

  toggleSidebar() {
    this.navService.openSportsBar.next(!this.navService.openSportsBar.value);
  }

  getLeagueDisplayName(leagueName: string): string {
    if (!leagueName) return "";
    const parts = leagueName.split(" / ");
    return parts.length > 1 ? parts.slice(1).join(" / ") : leagueName;
  }

  getSportIcon(name: string): string {
    const m: Record<string, string> = {
      Cricket: "sports_cricket", Soccer: "sports_soccer", Football: "sports_soccer",
      Tennis: "sports_tennis", Basketball: "sports_basketball",
      "Horse Racing": "sports_horse_racing", Greyhounds: "pets",
      Snooker: "sports_pool", Rugby: "sports_rugby",
      "Table Tennis": "sports_tennis", "E-Football": "sports_esports",
      "American Football": "sports_football", "Ice Hockey": "sports_hockey",
      Volleyball: "sports_volleyball", "E-sports +": "sports_esports",
      "E-Basketball": "sports_esports", Motorsports: "sports_motorsports",
    };
    return m[name] || "sports_score";
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
